/**
 * Résilience deploy Render / coupure réseau :
 * - détection maintenance (502/503/504)
 * - retry mutations critiques
 * - resynchronisation après reconnexion SSE
 */

export const DEPLOY_EVENTS = {
  MAINTENANCE_START: 'ucp-deploy-maintenance-start',
  MAINTENANCE_END: 'ucp-deploy-maintenance-end',
  SSE_RECONNECTED: 'ucp-sse-reconnected',
};

export const SSE_CHANNELS = {
  ACTIVITIES: 'activities',
  DASHBOARD_STATS: 'dashboard-stats',
  INTERVENANT_INTERVENTIONS: 'intervenant-interventions',
  ADMIN_INTERVENTIONS: 'admin-interventions',
  CLIENT_INTERVENTIONS: 'client-interventions',
  NOTIFICATIONS: 'notifications',
  MESSAGING: 'messaging',
  PLANNING: 'planning',
};

const GATEWAY_STATUSES = new Set([502, 503, 504]);
const CRITICAL_POST_RETRY_MAX = 2;
const CRITICAL_POST_RETRY_DELAYS_MS = [800, 1600];

const DENIED_RETRY_URL_PATTERNS = [
  /\/auth\//i,
  /\/stream\b/i,
  /\/activity-reports\/generate\b/i,
  /\/generate-pdf\b/i,
];

let gatewayFailureStreak = 0;
let maintenanceActive = false;

function dispatchDeployEvent(name, detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

export function recordGatewayFailure() {
  gatewayFailureStreak += 1;
  if (gatewayFailureStreak >= 2 && !maintenanceActive) {
    maintenanceActive = true;
    dispatchDeployEvent(DEPLOY_EVENTS.MAINTENANCE_START, { reason: 'gateway' });
  }
}

export function recordApiSuccess() {
  gatewayFailureStreak = 0;
  if (maintenanceActive) {
    maintenanceActive = false;
    dispatchDeployEvent(DEPLOY_EVENTS.MAINTENANCE_END, { reason: 'api_ok' });
  }
}

export function isDeployMaintenanceActive() {
  return maintenanceActive;
}

/** Tests unitaires uniquement. */
export function __resetDeployResilienceStateForTests() {
  gatewayFailureStreak = 0;
  maintenanceActive = false;
}

export function notifySseReconnected(channel, extra = {}) {
  dispatchDeployEvent(DEPLOY_EVENTS.SSE_RECONNECTED, { channel, ...extra });
  recordApiSuccess();
}

/**
 * Suivi par flux SSE : refetch léger uniquement après une vraie coupure (pas au 1er open).
 * P2 : touchActivity / isStale pour détecter un EventSource gelé hors foreground.
 * @param {string} channel
 * @param {{ staleMs?: number }} [options]
 */
export function createSseReconnectHandler(channel, options = {}) {
  const staleMs = Number(options.staleMs) > 0 ? Number(options.staleMs) : 90_000;
  let hadSuccessfulConnection = false;
  let wasDisconnected = false;
  let lastActivityAt = 0;

  return {
    markDisconnected() {
      if (hadSuccessfulConnection) wasDisconnected = true;
    },
    touchActivity() {
      lastActivityAt = Date.now();
    },
    isStale() {
      if (!hadSuccessfulConnection || !lastActivityAt) return false;
      return Date.now() - lastActivityAt > staleMs;
    },
    handleOpen(onRefetch) {
      lastActivityAt = Date.now();
      if (hadSuccessfulConnection && wasDisconnected) {
        notifySseReconnected(channel);
        if (typeof onRefetch === 'function') {
          try {
            onRefetch();
          } catch (err) {
            console.warn(`[deployResilience] refetch ${channel}:`, err?.message || err);
          }
        }
      }
      hadSuccessfulConnection = true;
      wasDisconnected = false;
    },
    handleError() {
      this.markDisconnected();
    },
  };
}

function isDeniedRetryUrl(url = '', method = 'get') {
  const u = String(url || '');
  const m = String(method || 'get').toLowerCase();
  // Création intervention : l'API gère duplicate_prevented ; retry gateway = risque course avant verrou.
  if (m === 'post' && /\/interventions\/?$/i.test(u)) return true;
  return DENIED_RETRY_URL_PATTERNS.some((re) => re.test(u));
}

function isGatewayOrNetworkError(error) {
  const status = Number(error?.response?.status || 0);
  if (GATEWAY_STATUSES.has(status)) return true;
  if (error?.response) return false;
  const code = String(error?.code || '').toUpperCase();
  const msg = String(error?.message || '').toLowerCase();
  return code === 'ECONNABORTED' || code === 'ERR_NETWORK' || msg.includes('network error');
}

export function isRetryableCriticalMutation(error, requestConfig) {
  if (!requestConfig) return false;
  const method = String(requestConfig.method || 'get').toLowerCase();
  if (!['post', 'put', 'patch', 'delete'].includes(method)) return false;
  if (requestConfig._skipCriticalRetry) return false;
  if (isDeniedRetryUrl(requestConfig.url || '', requestConfig.method)) return false;
  if (typeof FormData !== 'undefined' && requestConfig.data instanceof FormData) return false;

  const contentType =
    requestConfig?.headers?.['Content-Type'] ||
    requestConfig?.headers?.['content-type'] ||
    '';
  if (String(contentType).includes('multipart/form-data')) return false;

  const retryCount = Number(requestConfig._criticalRetryCount || 0);
  if (retryCount >= CRITICAL_POST_RETRY_MAX) return false;

  return isGatewayOrNetworkError(error);
}

export function getCriticalMutationRetryDelayMs(retryCount) {
  const idx = Math.min(Math.max(retryCount, 0), CRITICAL_POST_RETRY_DELAYS_MS.length - 1);
  return CRITICAL_POST_RETRY_DELAYS_MS[idx];
}

export async function retryCriticalMutation(apiFn, originalRequest, error) {
  if (!isRetryableCriticalMutation(error, originalRequest)) {
    return null;
  }
  recordGatewayFailure();
  const nextRetryCount = Number(originalRequest._criticalRetryCount || 0) + 1;
  const delayMs = getCriticalMutationRetryDelayMs(nextRetryCount - 1);
  await new Promise((resolve) => setTimeout(resolve, delayMs));
  return apiFn({
    ...originalRequest,
    _criticalRetryCount: nextRetryCount,
    _skipOfflineQueue: true,
    _skipCriticalRetry: false,
  });
}
